import React, { useState, useEffect, useCallback } from 'react';
import './AcademiaCarousel.css';
import { fetchAcademiaCarouselImages } from '../../services/api';

const AcademiaCarousel = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    // Fetch carousel images from API
    useEffect(() => {
        const loadImages = async () => {
            try {
                setLoading(true);
                const apiImages = await fetchAcademiaCarouselImages();
                
                if (apiImages && apiImages.length > 0) {
                    // Transform API response to image URLs
                    const transformedImages = apiImages.map(img => {
                        if (typeof img === 'string') {
                            return img;
                        }
                        return img.url || img.src || img;
                    });
                    setImages(transformedImages);
                } else {
                    setImages([]);
                }
            } catch (error) {
                console.error('Failed to load academia carousel images:', error);
                setImages([]);
            } finally {
                setLoading(false);
            }
        };

        loadImages();
    }, []);

    const nextSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, [images.length]);

    const prevSlide = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    useEffect(() => {
        if (!isHovered && images.length > 0) {
            const interval = setInterval(nextSlide, 5000);
            return () => clearInterval(interval);
        }
    }, [isHovered, nextSlide, images.length]);

    // Don't render until images are loaded
    if (loading) {
        return (
            <div className="academia-carousel-container" dir="ltr">
                <div className="academia-carousel-wrapper">
                    <div className="academia-carousel-loading">Loading...</div>
                </div>
            </div>
        );
    }

    if (images.length === 0) {
        return null;
    }

    return (
        <div
            className="academia-carousel-container"
            dir="ltr"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="academia-carousel-wrapper">
                <div
                    className="academia-carousel-slides"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {images.map((img, index) => {
                        const imgUrl = typeof img === 'string' ? img : (img.url || img.src || img);
                        const imgAlt = typeof img === 'object' && img.alt ? img.alt : `Academia ${index + 1}`;
                        return (
                            <div key={index} className="academia-carousel-slide">
                                <img src={imgUrl} alt={imgAlt} loading="lazy" />
                            </div>
                        );
                    })}
                </div>

                {/* Navigation Arrows */}
                <button className="academia-carousel-arrow prev" onClick={prevSlide} aria-label="Previous slide">
                    &#10094;
                </button>
                <button className="academia-carousel-arrow next" onClick={nextSlide} aria-label="Next slide">
                    &#10095;
                </button>

                {/* Dots */}
                <div className="academia-carousel-dots">
                    {images.map((_, index) => (
                        <button
                            key={index}
                            className={`academia-carousel-dot ${currentIndex === index ? 'active' : ''}`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AcademiaCarousel;

